import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { FileCheck } from "lucide-react";
import { PageHeader } from "@/components/common";
import { useTran } from "@/hooks";
import { useResponseSurveyStore } from "../hooks";
import ResponsesFilter from "../components/ResponsesFilter";
import SurveyResponseTable from "../components/SurveyResponseTable";
import { useAuthStore } from "@/features/auth/hooks";
import { PATHS } from "@/lib/router";
import { useNavigate } from "react-router-dom";

const SurveyResponsesPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const store = useResponseSurveyStore();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];

  useEffect(() => {
    store.initialize();
  }, []);

  useEffect(() => {
    if (!store.initializing) {
      store.fetchResponses();
    }
  }, [
    store.pagination.page,
    store.pagination.limit,
    store.filters.search,
    store.filters.surveyId,
    store.filters.site,
    store.filters.status,
    store.filters.priorityLabel,
    store.filters.sort,
  ]);

  const handleView = (responseId) => {
    navigate(PATHS.RESPONSES_DETAIL.replace(":responseId", responseId));
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <PageHeader
        title={t("Survey Responses")}
        description={
          !store.initializing
            ? `${store.stats.total} responses · ${store.stats.surveyCount} surveys · ${store.stats.newCount} new`
            : t("Manage your survey responses.")
        }
        icon={FileCheck}
      />

      {/* ── Filters ── */}
      <ResponsesFilter
        filters={store.filters}
        stats={store.stats}
        onSearch={(val) => store.setSearch(val)}
        onSurveyChange={(val) => store.setSurveyFilter(val)}
        onSiteChange={(val) => store.setSiteFilter(val)}
        onStatusChange={(val) => store.setStatus(val)}
        onPriorityChange={(val) => store.setPriorityLabel(val)}
        onSortChange={(val) => store.setSort(val)}
      />

      {/* ── Table ── */}
      <SurveyResponseTable
        responses={store.responses}
        loading={store.initializing || store.loading}
        page={store.pagination.page}
        pageSize={store.pagination.limit}
        total={store.paginationInfo.total}
        onPageChange={(page) => store.setPage(page)}
        onPageSizeChange={(size) => store.setRowsPerPage(size)}
        onView={handleView}
      />
    </div>
  );
});

export default SurveyResponsesPage;
